// MenuChat/MenuChat.tsx
// Split-screen AI menu chat.
// Left: phone-frame app preview faithful to the real customer-facing UI.
// Right: chat with apply flow, image uploads, delete+restart.
// Mobile: preview hidden by default, toggled via top-bar button.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  getFirestore,
  doc,
  onSnapshot,
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import app from '../../../Firebase';
import { applyChangesToMenu, MenuChange } from './applyChangesToMenu';
import './MenuChat.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Store {
  storeId?: string;
  name: string;
  menuData?: any;
  [key: string]: any;
}

interface MenuChatProps {
  store: Store;
  functions: Functions;
  appId: string;
  onBack: () => void;
}

type MessageRole = 'user' | 'assistant' | 'system';

interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: Date;
  changeSet?: ChangeSetResponse;
  jobId?: string;
  appliedAt?: Date;
  images?: string[];
}

interface ChangeSetResponse {
  explanation: string;
  applyMode: 'auto' | 'preview';
  changes: MenuChange[];
}

type JobStatus = 'pending' | 'processing' | 'complete' | 'error';

interface MenuJob {
  status: JobStatus;
  result?: ChangeSetResponse | { menuData: any };
  error?: string;
  progress?: { current: number; total: number; currentStep: string };
}

type ApplyState =
  | { phase: 'idle' }
  | { phase: 'awaiting_confirm'; pendingMessageId: string }
  | { phase: 'applying' }
  | { phase: 'applied'; appliedAt: Date };

type ChatMode = 'creation' | 'edit';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}

function compressMenuForChat(menuData: any): any {
  if (!menuData) return menuData;
  return {
    menuSettings: (menuData.menuSettings || []).map((ms: any) => ({
      menuName: ms.menuName, enabled: ms.enabled,
      categories: (ms.categories || []).map((c: any) => ({ id: c.id, name: c.name, order: c.order })),
    })),
    menu: (menuData.menu || []).filter((item: any) => !item.isDeleted).map((item: any) => ({
      productId: item.productId, productName: item.productName,
      productDescription: item.productDescription, productPrice: item.productPrice,
      category: item.category, menus: item.menus, order: item.order,
      ...(item.isDisabled && { isDisabled: true }),
      ...(item.outOfStock && { outOfStock: true }),
      ...(item.additionalOptions?.length && { additionalOptions: item.additionalOptions }),
      ...(item.userChoices?.length && { userChoices: item.userChoices }),
    })),
  };
}

function describeChange(c: MenuChange): string {
  switch (c.type) {
    case 'ADD_MENU':            return `add menu "${c.menuName}"`;
    case 'ADD_CATEGORY':        return `add category "${c.categoryName}" to ${c.menuName}`;
    case 'ADD_ITEM':            return `add "${c.item?.productName}" to ${c.categoryName}`;
    case 'ADD_HIDDEN_ITEM':     return `add hidden add-on "${c.item?.productName}"`;
    case 'UPDATE_ITEM':         return `update "${c.productName}": ${Object.keys(c.after || {}).map((f: string) => `${f} → ${(c.after as any)[f]}`).join(', ')}`;
    case 'UPDATE_USER_CHOICES': return `update choices on "${c.productName}"`;
    case 'MOVE_ITEM':           return `move "${c.productName}" → ${c.toMenu} › ${c.toCategory}`;
    case 'MOVE_CATEGORY':       return `move category "${c.categoryName}" → ${c.toMenu}`;
    case 'REMOVE_ITEM':         return `remove "${c.productName}"`;
    case 'REMOVE_CATEGORY':     return `remove category "${c.categoryName}"`;
    case 'REORDER':             return `reorder ${c.scope} in ${c.menuName}`;
    default:                    return c.type;
  }
}

function buildConversationContext(messages: ChatMessage[]): string {
  const relevant = messages.filter(m => m.role !== 'system');
  if (relevant.length === 0) return 'No prior conversation.';
  return relevant.map(m => {
    if (m.role === 'user') return `User: ${m.content}${m.images?.length ? ` [+${m.images.length} image(s)]` : ''}`;
    if (m.changeSet) {
      if (m.changeSet.changes.length === 0) return `Assistant: ${m.changeSet.explanation || m.content} [No changes proposed]`;
      return `Assistant: ${m.changeSet.explanation || m.content} [Proposed: ${m.changeSet.changes.map(describeChange).join('; ')}]${m.appliedAt ? ' [APPLIED]' : ' [NOT applied]'}`;
    }
    return `Assistant: ${m.content}`;
  }).join('\n');
}

function changeTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    ADD_MENU: 'Add Menu', ADD_CATEGORY: 'Add Category', ADD_ITEM: 'Add Item', ADD_HIDDEN_ITEM: 'Add Add-on',
    UPDATE_ITEM: 'Update Item', UPDATE_USER_CHOICES: 'Update Choices',
    MOVE_ITEM: 'Move Item', MOVE_CATEGORY: 'Move Category',
    REMOVE_ITEM: 'Remove Item', REMOVE_CATEGORY: 'Remove Category', REORDER: 'Reorder',
  };
  return labels[type] || type;
}

function changeTypeColor(type: string): string {
  if (type.startsWith('ADD'))    return '#22c55e';
  if (type.startsWith('UPDATE')) return '#f59e0b';
  if (type.startsWith('MOVE'))   return '#3b82f6';
  if (type.startsWith('REMOVE')) return '#ef4444';
  return '#9ca3af';
}

async function filesToBase64(files: File[]): Promise<string[]> {
  return Promise.all(files.map(f => new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload  = () => res(r.result as string);
    r.onerror = rej;
    r.readAsDataURL(f);
  })));
}

// ---------------------------------------------------------------------------
// ChangeCard
// ---------------------------------------------------------------------------

const ChangeCard: React.FC<{
  change: MenuChange;
  onToggle?: (id: string) => void;
  showToggle?: boolean;
}> = ({ change, onToggle, showToggle = false }) => {
  const color = changeTypeColor(change.type);
  const label = changeTypeLabel(change.type);

  const renderDetails = () => {
    switch (change.type) {
      case 'ADD_MENU':        return <span>New menu: <strong>{change.menuName}</strong></span>;
      case 'ADD_CATEGORY':    return <span>New category <strong>{change.categoryName}</strong> in <em>{change.menuName}</em></span>;
      case 'ADD_ITEM':        return <span><strong>{change.item?.productName}</strong> → <em>{change.categoryName}</em> · ${(change.item?.productPrice ?? 0).toFixed(2)}</span>;
      case 'UPDATE_ITEM':
        return (
          <div className="change-card-diff">
            <strong>{change.productName}</strong>
            {Object.keys(change.before || {}).map(f => (
              <div key={f} className="diff-row">
                <span className="diff-field">{f}</span>
                <span className="diff-before">{String(change.before[f])}</span>
                <span className="diff-arrow">→</span>
                <span className="diff-after">{String(change.after[f])}</span>
              </div>
            ))}
          </div>
        );
      case 'MOVE_ITEM':       return <span><strong>{change.productName}</strong>: <em>{change.fromCategory}</em> → <em>{change.toCategory}</em> ({change.toMenu})</span>;
      case 'MOVE_CATEGORY':   return <span>Category <strong>{change.categoryName}</strong>: <em>{change.fromMenu}</em> → <em>{change.toMenu}</em></span>;
      case 'REMOVE_ITEM':     return <span>Remove <strong>{change.productName}</strong> from <em>{change.categoryName}</em></span>;
      case 'REMOVE_CATEGORY': return <span>Remove category <strong>{change.categoryName}</strong>{change.itemCount > 0 && ` (${change.itemCount} items)`}</span>;
      case 'REORDER':         return <span>Reorder {change.scope} in <em>{change.menuName}</em>{change.categoryName && ` › ${change.categoryName}`}</span>;
      case 'UPDATE_USER_CHOICES':
        return (
          <div className="change-card-diff">
            <strong>{change.productName}</strong>
            <div className="choices-diff">
              <div className="choices-diff-col">
                <span className="choices-diff-label">Before</span>
                {(change.before || []).length === 0 ? <span className="choices-empty">No choices</span>
                  : (change.before || []).map((c: any, i: number) => (
                    <span key={i} className="choice-group-pill choice-group-pill--before">{c.name} ({c.type}) · {c.choices?.length ?? 0} opts</span>
                  ))}
              </div>
              <span className="diff-arrow choices-arrow">→</span>
              <div className="choices-diff-col">
                <span className="choices-diff-label">After</span>
                {(change.after || []).length === 0 ? <span className="choices-empty">No choices</span>
                  : (change.after || []).map((c: any, i: number) => (
                    <span key={i} className="choice-group-pill choice-group-pill--after">{c.name} ({c.type}) · {c.choices?.length ?? 0} opts</span>
                  ))}
              </div>
            </div>
          </div>
        );
      case 'ADD_HIDDEN_ITEM':
        return (
          <div className="change-card-hidden-item">
            <div className="hidden-item-header">
              <strong>{change.item?.productName}</strong>
              <span className="hidden-item-price">+${(change.item?.productPrice ?? 0).toFixed(2)}</span>
              <span className="hidden-item-badge">Hidden add-on</span>
            </div>
            {change.item?.productDescription && <p className="hidden-item-desc">{change.item.productDescription}</p>}
            {(change.linkToProductIds || []).length > 0 && (
              <span className="hidden-item-links-label">Linked to {(change.linkToProductIds || []).length} item{(change.linkToProductIds || []).length !== 1 ? 's' : ''}</span>
            )}
          </div>
        );
      default: return <span className="change-card-raw">{JSON.stringify(change)}</span>;
    }
  };

  return (
    <div className={`change-card ${!change.approved ? 'change-card--skipped' : ''}`} style={{ borderLeftColor: change.approved ? color : '#374151' }}>
      <div className="change-card-top">
        <span className="change-type-badge" style={change.approved ? { background: `${color}22`, color } : { background: '#1f2937', color: '#6b7280' }}>{label}</span>
        {showToggle && onToggle && (
          <button className={`change-toggle-btn ${change.approved ? 'approved' : 'skipped'}`} onClick={() => onToggle(change.id)}>
            {change.approved ? '✓ Include' : '✗ Skip'}
          </button>
        )}
      </div>
      <div className="change-card-details">{renderDetails()}</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// PreviewProductModal — mirrors ProductCardAdditionalOptions for the preview
// Self-contained dark-mode version, no external dependencies
// ---------------------------------------------------------------------------

const PreviewProductModal: React.FC<{
  item: any;
  productMap: Map<string, any>;
  menuSettings: any[];
  activeMenu: string;
  onClose: () => void;
}> = ({ item, productMap, menuSettings, activeMenu, onClose }) => {
  const [selectedAddons, setSelectedAddons]   = useState<string[]>([]);
  const [selectedChoices, setSelectedChoices] = useState<{name: string; value: string; parent?: string}[]>([]);
  const [expandedAddons, setExpandedAddons]   = useState<Record<string, boolean>>({});
  const [quantity, setQuantity]               = useState(1);
  const [totalPrice, setTotalPrice]           = useState(item.productPrice ?? 0);

  const additionalOptions = (item.additionalOptions || [])
    .map((id: string) => productMap.get(id))
    .filter(Boolean)
    .filter((o: any) => !o.isDeleted && !o.isDisabled);

  // Group add-ons by category
  const categoryIds: string[] = [];
  additionalOptions.forEach((opt: any) => {
    const cid = opt.category || 'extras';
    if (!categoryIds.includes(cid)) categoryIds.push(cid);
  });

  const getCategoryName = (id: string) => {
    if (id === 'extras') return 'Extras';
    return menuSettings.flatMap((ms: any) => ms.categories || []).find((c: any) => c.id === id)?.name || id;
  };

  const isChoiceSelected = (name: string, value: string) =>
    selectedChoices.some(c => !c.parent && c.name === name && c.value === value);

  const isAddonChoiceSelected = (addonId: string, name: string, value: string) =>
    selectedChoices.some(c => c.parent === addonId && c.name === name && c.value === value);

  const handleChoiceToggle = (name: string, value: string, type: string, limit?: number) => {
    setSelectedChoices(prev => {
      const existing = prev.filter(c => !c.parent && c.name === name);
      const others   = prev.filter(c => c.parent || c.name !== name);
      if (type === 'SELECT_ONE') {
        return existing.some(c => c.value === value) ? others : [...others, { name, value }];
      } else if (type === 'SELECT_ANY') {
        return existing.some(c => c.value === value)
          ? [...others, ...existing.filter(c => c.value !== value)]
          : [...prev, { name, value }];
      } else { // SELECT_LIMIT
        if (existing.some(c => c.value === value)) return [...others, ...existing.filter(c => c.value !== value)];
        return existing.length < (limit || 1) ? [...prev, { name, value }] : prev;
      }
    });
  };

  const handleAddonChoiceToggle = (addonId: string, name: string, value: string, type: string, limit?: number) => {
    setSelectedChoices(prev => {
      const existing = prev.filter(c => c.parent === addonId && c.name === name);
      const others   = prev.filter(c => !(c.parent === addonId && c.name === name));
      if (type === 'SELECT_ONE') {
        return existing.some(c => c.value === value) ? others : [...others, { name, value, parent: addonId }];
      } else if (type === 'SELECT_ANY') {
        return existing.some(c => c.value === value)
          ? [...others, ...existing.filter(c => c.value !== value)]
          : [...prev, { name, value, parent: addonId }];
      } else {
        if (existing.some(c => c.value === value)) return [...others, ...existing.filter(c => c.value !== value)];
        return existing.length < (limit || 1) ? [...prev, { name, value, parent: addonId }] : prev;
      }
    });
  };

  const handleAddonToggle = (opt: any) => {
    const isSelected = selectedAddons.includes(opt.productId);
    if (isSelected) {
      setTotalPrice((p: number) => p - opt.productPrice);
      setSelectedAddons(prev => prev.filter(id => id !== opt.productId));
      setSelectedChoices(prev => prev.filter(c => c.parent !== opt.productId));
      setExpandedAddons(prev => { const n = {...prev}; delete n[opt.productId]; return n; });
    } else {
      setTotalPrice((p: number) => p + opt.productPrice);
      setSelectedAddons(prev => [...prev, opt.productId]);
      if (opt.userChoices?.length > 0) setExpandedAddons(prev => ({...prev, [opt.productId]: true}));
    }
  };

  return (
    <div className="ppm-overlay" onClick={onClose}>
      <div className="ppm-sheet" onClick={e => e.stopPropagation()}>

        {/* Scrollable content */}
        <div className="ppm-scroll">
          <button className="ppm-close" onClick={onClose}>×</button>

          {item.productThumbnailUrl && (
            <img src={item.productThumbnailUrl} alt={item.productName} className="ppm-hero-img" />
          )}

          <h2 className="ppm-title">{item.productName}</h2>
          {item.productDescription && <p className="ppm-desc">{item.productDescription}</p>}

          {/* userChoices */}
          {(item.userChoices || []).map((uc: any, i: number) => (
            <div key={i} className="ppm-section">
              <p className="ppm-section-title">
                {uc.name}
                {uc.optionRequired && uc.type !== 'SELECT_LIMIT' && <span className="ppm-required"> *</span>}
                {uc.type === 'SELECT_LIMIT' && <span className="ppm-limit"> (Select up to {uc.limit})</span>}
              </p>
              {(uc.choices || []).map((choice: string, ci: number) => {
                const sel = isChoiceSelected(uc.name, choice);
                return (
                  <div key={ci} className={`ppm-option ${sel ? 'selected' : ''}`} onClick={() => handleChoiceToggle(uc.name, choice, uc.type, uc.limit)}>
                    <div className="ppm-option-left">
                      <input type={uc.type === 'SELECT_ONE' ? 'radio' : 'checkbox'} checked={sel} readOnly className="ppm-checkbox" />
                      <span className="ppm-option-label">{choice}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* additionalOptions */}
          {categoryIds.map((catId, ci) => (
            <div key={ci} className="ppm-section">
              <p className="ppm-section-title">{getCategoryName(catId)}</p>
              {additionalOptions.filter((o: any) => (o.category || 'extras') === catId).map((opt: any, oi: number) => {
                const sel       = selectedAddons.includes(opt.productId);
                const hasChoices = opt.userChoices?.length > 0;
                const isExpanded = expandedAddons[opt.productId];
                return (
                  <div key={oi}>
                    <div className={`ppm-option ${sel ? 'selected' : ''}`} onClick={() => handleAddonToggle(opt)}>
                      <div className="ppm-option-left">
                        <input type="checkbox" checked={sel} readOnly className="ppm-checkbox" />
                        {opt.productThumbnailUrl
                          ? <img src={opt.productThumbnailUrl} alt={opt.productName} className="ppm-addon-img" />
                          : <div className="ppm-addon-placeholder">🍽</div>
                        }
                        <span className="ppm-option-label">{opt.productName}</span>
                        {hasChoices && sel && (
                          <button className="ppm-expand-btn" onClick={e => { e.stopPropagation(); setExpandedAddons(p => ({...p, [opt.productId]: !p[opt.productId]})); }}>
                            {isExpanded ? '▲' : '▼'}
                          </button>
                        )}
                      </div>
                      <span className="ppm-addon-price">+£{opt.productPrice?.toFixed(2)}</span>
                    </div>
                    {sel && hasChoices && isExpanded && (
                      <div className="ppm-addon-choices">
                        {(opt.userChoices || []).map((auc: any, ai: number) => (
                          <div key={ai} style={{marginBottom: 8}}>
                            <p className="ppm-section-title" style={{fontSize: 12, marginBottom: 6}}>
                              {auc.name}
                              {auc.optionRequired && <span className="ppm-required"> *</span>}
                            </p>
                            {(auc.choices || []).map((ac: string, aci: number) => {
                              const asel = isAddonChoiceSelected(opt.productId, auc.name, ac);
                              return (
                                <div key={aci} className={`ppm-option ${asel ? 'selected' : ''}`} onClick={() => handleAddonChoiceToggle(opt.productId, auc.name, ac, auc.type, auc.limit)}>
                                  <div className="ppm-option-left">
                                    <input type={auc.type === 'SELECT_ONE' ? 'radio' : 'checkbox'} checked={asel} readOnly className="ppm-checkbox" />
                                    <span className="ppm-option-label" style={{fontSize: 13}}>{ac}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Sticky footer */}
        <div className="ppm-footer">
          <div className="ppm-quantity-row">
            <span className="ppm-qty-label">Quantity</span>
            <select className="ppm-qty-select" value={quantity} onChange={e => setQuantity(Number(e.target.value))}>
              {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <button className="ppm-add-btn">
            Add to Cart — £{(totalPrice * quantity).toFixed(2)}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// AppMenuViewer — faithful recreation of the real customer-facing app UI
// Dark mode, clickable items with product modal
// ---------------------------------------------------------------------------

const AppMenuViewer: React.FC<{ menuData: any; storeName: string }> = ({ menuData, storeName }) => {
  const [activeMenu, setActiveMenu]         = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [selectedItem, setSelectedItem]     = useState<any | null>(null);
  const [showJson, setShowJson]             = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const visibleMenus = (menuData?.menuSettings || []).filter((ms: any) => ms.enabled !== false && ms.menuName !== 'Additional Options');

  useEffect(() => {
    if (visibleMenus.length > 0 && !activeMenu) {
      setActiveMenu(visibleMenus[0].menuName);
    }
  }, [menuData]);

  useEffect(() => {
    const ms = visibleMenus.find((m: any) => m.menuName === activeMenu);
    if (ms?.categories?.length > 0) setActiveCategory(ms.categories[0].id);
  }, [activeMenu]);

  if (!menuData || visibleMenus.length === 0) {
    return (
      <div className="app-preview-empty">
        <div className="app-preview-empty-icon">🍽️</div>
        <p>Upload menu images to see a preview</p>
      </div>
    );
  }

  const activeMenuSettings = visibleMenus.find((ms: any) => ms.menuName === activeMenu);
  const categories = (activeMenuSettings?.categories || []).filter((cat: any) =>
    (menuData.menu || []).some((item: any) => item.category === cat.id && item.menus?.includes(activeMenu) && !item.isDeleted && !item.isDisabled)
  );

  const productMap = new Map<string, any>();
  (menuData.menu || []).forEach((item: any) => productMap.set(item.productId, item));

  const getFilteredItems = (categoryId: string) =>
    (menuData.menu || []).filter((item: any) =>
      item.category === categoryId &&
      item.menus?.includes(activeMenu) &&
      !item.isDeleted && !item.isDisabled &&
      item.menus?.[0] !== 'Additional Options'
    ).sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

  const scrollToCategory = (catId: string) => {
    setActiveCategory(catId);
    const el = categoryRefs.current[catId];
    if (el && containerRef.current) {
      const stickyHeight = 90;
      const containerTop = containerRef.current.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      containerRef.current.scrollTop += (elTop - containerTop) - stickyHeight;
    }
  };

  const handleItemClick = (item: any) => {
    if (item.productPrice && item.productPrice > 0) setSelectedItem(item);
  };

  return (
    <div className="app-preview-inner" ref={containerRef} style={selectedItem ? {overflow: "hidden"} : undefined}>

      {/* Product modal */}
      {selectedItem && (
        <PreviewProductModal
          item={selectedItem}
          productMap={productMap}
          menuSettings={menuData.menuSettings || []}
          activeMenu={activeMenu}
          onClose={() => setSelectedItem(null)}
        />
      )}

      {/* JSON viewer overlay */}
      {showJson && (
        <div className="ppm-overlay" onClick={() => setShowJson(false)}>
          <div className="app-preview-json-sheet" onClick={e => e.stopPropagation()}>
            <div className="app-preview-json-header">
              <span className="app-preview-json-title">Menu JSON</span>
              <button className="ppm-close" onClick={() => setShowJson(false)}>×</button>
            </div>
            <pre className="app-preview-json-body">
              {JSON.stringify(menuData, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* App header */}
      <div className="app-preview-header">
        <span className="app-preview-store-name">{storeName}</span>
        <button className="app-preview-json-btn" onClick={() => setShowJson(true)} title="View menu JSON">
          {'{ }'}
        </button>
      </div>

      {/* Sticky: menu tabs + category bar */}
      <div className="app-preview-sticky">
        {visibleMenus.length > 1 && (
          <div className="app-preview-menu-tabs">
            {visibleMenus.map((ms: any) => (
              <button key={ms.menuName} className={`app-preview-menu-tab ${activeMenu === ms.menuName ? 'active' : ''}`} onClick={() => setActiveMenu(ms.menuName)}>
                {ms.menuName}
              </button>
            ))}
          </div>
        )}
        {categories.length > 0 && (
          <div className="app-preview-cat-bar">
            {categories.map((cat: any) => (
              <button key={cat.id} className={`app-preview-cat-btn ${activeCategory === cat.id ? 'active' : ''}`} onClick={() => scrollToCategory(cat.id)}>
                {cat.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Product list */}
      <div className="app-preview-products">
        {categories.map((cat: any) => {
          const items = getFilteredItems(cat.id);
          if (items.length === 0) return null;
          return (
            <div key={cat.id} ref={el => { categoryRefs.current[cat.id] = el; }}>
              <div className="app-preview-cat-heading">{cat.name}</div>
              {items.map((item: any) => (
                <div key={item.productId} className="app-preview-product-card" onClick={() => handleItemClick(item)}>
                  <div className="app-preview-product-info">
                    <span className="app-preview-product-name">{item.productName}</span>
                    {item.productDescription && <span className="app-preview-product-desc">{item.productDescription}</span>}
                    {item.additionalOptions?.length > 0 && (
                      <div className="app-preview-product-addons">
                        {item.additionalOptions.slice(0, 3).map((id: string) => {
                          const addon = productMap.get(id);
                          return addon ? <span key={id} className="app-preview-addon-pill">+{addon.productName}</span> : null;
                        })}
                        {item.additionalOptions.length > 3 && <span className="app-preview-addon-pill">+{item.additionalOptions.length - 3} more</span>}
                      </div>
                    )}
                    {item.productPrice > 0 && <span className="app-preview-product-price">£{item.productPrice.toFixed(2)}</span>}
                  </div>
                  {item.productThumbnailUrl && (
                    <div className="app-preview-product-img-wrap">
                      <img src={item.productThumbnailUrl} alt={item.productName} className="app-preview-product-img" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
        <div className="app-preview-bottom-fade" />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const MenuChat: React.FC<MenuChatProps> = ({ store, functions, appId, onBack }) => {
  const [currentMenuData, setCurrentMenuData] = useState<any>(store.menuData ?? null);
  const [chatMode, setChatMode]               = useState<ChatMode>(store.menuData?.menu?.length ? 'edit' : 'creation');
  const [menuLoading, setMenuLoading]         = useState(true);

  // Fetch the live menu from dineplatform on mount — Firestore may be stale
  useEffect(() => {
    if (!store.storeId) { setMenuLoading(false); return; }
    const url = `https://www.dineplatform.com/r/${store.storeId}/restaurants.json`;
    fetch(url)
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(json => {
        const data = json[store.storeId!];
        if (data?.menu?.length) {
          setCurrentMenuData(data);
          setChatMode('edit');
        }
      })
      .catch(err => {
        console.warn('MenuChat: failed to fetch live menu, using Firestore copy:', err);
      })
      .finally(() => setMenuLoading(false));
  }, [store.storeId]);

  const [messages, setMessages]               = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue]           = useState('');
  const [isSubmitting, setIsSubmitting]       = useState(false);
  const [activeJobId, setActiveJobId]         = useState<string | null>(null);
  const [applyState, setApplyState]           = useState<ApplyState>({ phase: 'idle' });
  const [changeApprovals, setChangeApprovals] = useState<Record<string, Map<string, boolean>>>({});
  const [attachedFiles, setAttachedFiles]     = useState<File[]>([]);
  const [attachedPreviews, setAttachedPreviews] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting]               = useState(false);
  const [menuNames, setMenuNames]             = useState<string[]>(['Main Menu', 'Drinks Menu', 'Desserts Menu']);
  const [newMenuNameInput, setNewMenuNameInput] = useState('');

  // Mobile preview toggle
  const [showMobilePreview, setShowMobilePreview] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef       = useRef<HTMLTextAreaElement>(null);
  const fileInputRef   = useRef<HTMLInputElement>(null);
  const db = getFirestore(app);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { return () => { attachedPreviews.forEach(url => URL.revokeObjectURL(url)); }; }, [attachedPreviews]);

  // Watch active job
  useEffect(() => {
    if (!activeJobId) return;
    const unsub = onSnapshot(doc(db, 'menuProcessingJobs', activeJobId), snap => {
      if (!snap.exists()) return;
      const job = snap.data() as MenuJob;

      if (job.status === 'complete' && job.result) {
        if (chatMode === 'creation' && (job.result as any).menuData) {
          const newMenu = (job.result as any).menuData;
          setCurrentMenuData(newMenu);
          setChatMode('edit');
          setMessages(prev => prev.map(m => m.jobId === activeJobId && m.role === 'assistant'
            ? { ...m, content: `✓ Menu created with ${newMenu.menu?.filter((i: any) => !i.isDeleted && i.menus?.[0] !== 'Additional Options').length ?? 0} items. Review it in the preview, then type apply to push live.` }
            : m
          ));
          setApplyState({ phase: 'awaiting_confirm', pendingMessageId: activeJobId });
          setTimeout(() => inputRef.current?.focus(), 100);
        } else {
          const cs = (job.result as any) as ChangeSetResponse;
          const approvalMap = new Map<string, boolean>(cs.changes.map(c => [c.id, true]));
          setMessages(prev => prev.map(m => m.jobId === activeJobId && m.role === 'assistant'
            ? { ...m, content: cs.explanation || 'Here\'s what I found:', changeSet: cs }
            : m
          ));
          setChangeApprovals(prev => ({ ...prev, [activeJobId]: approvalMap }));
          if (cs.applyMode === 'auto' && cs.changes.length > 0) {
            setApplyState({ phase: 'awaiting_confirm', pendingMessageId: activeJobId });
            setTimeout(() => inputRef.current?.focus(), 100);
          }
        }
        setActiveJobId(null);
        setIsSubmitting(false);
      }
      if (job.status === 'error') {
        setMessages(prev => prev.map(m => m.jobId === activeJobId && m.role === 'assistant'
          ? { ...m, content: `Sorry, something went wrong: ${job.error || 'Unknown error'}. Please try again.` }
          : m
        ));
        setActiveJobId(null);
        setIsSubmitting(false);
      }
      if (job.status === 'processing' && job.progress) {
        setMessages(prev => prev.map(m => m.jobId === activeJobId && m.role === 'assistant'
          ? { ...m, content: job.progress!.currentStep }
          : m
        ));
      }
    });
    return unsub;
  }, [activeJobId, db, chatMode]);

  // File attach
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/') || f.type === 'application/pdf');
    if (!files.length) return;
    const previews = files.map(f => f.type.startsWith('image/') ? URL.createObjectURL(f) : '');
    setAttachedFiles(prev => [...prev, ...files]);
    setAttachedPreviews(prev => [...prev, ...previews]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (idx: number) => {
    if (attachedPreviews[idx]) URL.revokeObjectURL(attachedPreviews[idx]);
    setAttachedFiles(prev => prev.filter((_, i) => i !== idx));
    setAttachedPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  // Delete menu
  const handleDeleteMenu = async () => {
    setIsDeleting(true);
    try {
      const overwriteFn = httpsCallable(functions, 'overwriteStoreMenu');
      await overwriteFn({
        storeData: { storeId: store.storeId, name: store.name, menuData: { menu: [], menuSettings: [] } },
        appId,
        historyEntry: {
          previousMenuData: currentMenuData,
          newMenuData: null,
          appliedChanges: [],
          skippedChanges: [],
          changeSetExplanation: 'User deleted menu and started over',
          source: 'manual_delete',
        },
      });
      setCurrentMenuData(null);
      setChatMode('creation');
      setMessages([]);
      setApplyState({ phase: 'idle' });
      setChangeApprovals({});
      setMenuNames(['Main Menu', 'Drinks Menu', 'Desserts Menu']);
      setShowDeleteConfirm(false);
    } catch (err: any) {
      console.error('Delete failed:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Creation submit
  const handleCreateMenuFromImages = useCallback(async () => {
    if (attachedFiles.length === 0 || isSubmitting) return;
    setIsSubmitting(true);
    const images = await filesToBase64(attachedFiles);
    const imagePreviews = [...attachedPreviews];
    setAttachedFiles([]); setAttachedPreviews([]);
    const placeholderId = generateId();
    setMessages([
      { id: generateId(), role: 'user', content: `Building menu from ${images.length} image${images.length !== 1 ? 's' : ''}…`, timestamp: new Date(), images: imagePreviews },
      { id: placeholderId, role: 'assistant', content: 'Processing your menu images…', timestamp: new Date(), jobId: placeholderId },
    ]);
    try {
      const submitJob = httpsCallable(functions, 'submitMenuJob');
      const result = await submitJob({ jobType: 'initial_creation', appId, storeId: store.storeId, menuNames, images, instructions: inputValue.trim() || undefined });
      const { jobId } = result.data as { jobId: string };
      setMessages(prev => prev.map(m => m.id === placeholderId ? { ...m, jobId } : m));
      setActiveJobId(jobId);
      setInputValue('');
    } catch (err: any) {
      setMessages(prev => prev.map(m => m.id === placeholderId ? { ...m, content: 'Failed to process images. Please try again.' } : m));
      setIsSubmitting(false);
    }
  }, [attachedFiles, attachedPreviews, isSubmitting, functions, appId, store, menuNames, inputValue]);

  // Approvals
  const toggleChangeApproval = useCallback((messageId: string, changeId: string) => {
    setChangeApprovals(prev => {
      const map = new Map(prev[messageId] || new Map());
      map.set(changeId, !map.get(changeId));
      return { ...prev, [messageId]: map };
    });
  }, []);

  const getChangesWithApprovals = useCallback((msg: ChatMessage): MenuChange[] => {
    if (!msg.changeSet) return [];
    const approvals = changeApprovals[msg.jobId || msg.id];
    return msg.changeSet.changes.map(c => ({ ...c, approved: approvals ? (approvals.get(c.id) ?? c.approved) : c.approved }));
  }, [changeApprovals]);

  const initiateApply = useCallback((messageId: string) => {
    setApplyState({ phase: 'awaiting_confirm', pendingMessageId: messageId });
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const cancelApply = useCallback(() => setApplyState({ phase: 'idle' }), []);

  const executeApply = useCallback(async (messageId: string) => {
    const isCreationApply = chatMode === 'creation' || !messages.find(m => (m.id === messageId || m.jobId === messageId) && m.changeSet);
    if (isCreationApply) {
      if (!currentMenuData) { setApplyState({ phase: 'idle' }); return; }
      setApplyState({ phase: 'applying' });
      try {
        const result = await httpsCallable(functions, 'overwriteStoreMenu')({
          storeData: { storeId: store.storeId, name: store.name, menuData: currentMenuData },
          appId,
          historyEntry: {
            previousMenuData: null,
            newMenuData: currentMenuData,
            appliedChanges: [],
            skippedChanges: [],
            changeSetExplanation: 'Initial menu creation via AI chat',
            source: 'ai_chat_creation',
          },
        });
        if (!(result.data as any).success) throw new Error((result.data as any).error || 'Failed');
        setApplyState({ phase: 'applied', appliedAt: new Date() });
        setChatMode('edit');
        setMessages(prev => [...prev, { id: generateId(), role: 'assistant' as MessageRole, content: '✓ Menu pushed to your live store. You can continue chatting to make further changes.', timestamp: new Date() }]);
        setTimeout(() => setApplyState({ phase: 'idle' }), 3000);
      } catch (err: any) {
        setApplyState({ phase: 'idle' });
        setMessages(prev => [...prev, { id: generateId(), role: 'assistant' as MessageRole, content: `Failed to push menu: ${err.message}`, timestamp: new Date() }]);
      }
      return;
    }

    const msg = messages.find(m => m.id === messageId || m.jobId === messageId);
    if (!msg?.changeSet) return;
    const changes = getChangesWithApprovals(msg);
    const approved = changes.filter(c => c.approved);
    if (approved.length === 0) { setApplyState({ phase: 'idle' }); return; }
    setApplyState({ phase: 'applying' });
    try {
      const newMenuData = applyChangesToMenu(currentMenuData, approved);
      const result = await httpsCallable(functions, 'overwriteStoreMenu')({
        storeData: { storeId: store.storeId, name: store.name, menuData: newMenuData },
        appId,
        historyEntry: {
          previousMenuData: currentMenuData,
          newMenuData,
          appliedChanges: approved,
          skippedChanges: changes.filter(c => !c.approved),
          changeSetExplanation: msg.changeSet.explanation,
          source: 'ai_chat',
          conversationSummary: buildConversationContext(messages.slice(-20)),
        },
      });
      if (!(result.data as any).success) throw new Error((result.data as any).error || 'Failed');
      setCurrentMenuData(newMenuData);
      setMessages(prev => prev.map(m => (m.id === messageId || m.jobId === messageId) ? { ...m, appliedAt: new Date() } : m));
      setApplyState({ phase: 'applied', appliedAt: new Date() });
      setMessages(prev => [...prev, { id: generateId(), role: 'assistant' as MessageRole, content: `✓ Applied ${approved.length} change${approved.length !== 1 ? 's' : ''} to your live menu.`, timestamp: new Date() }]);
      setTimeout(() => setApplyState({ phase: 'idle' }), 3000);
    } catch (err: any) {
      setApplyState({ phase: 'idle' });
      setMessages(prev => [...prev, { id: generateId(), role: 'assistant' as MessageRole, content: `Failed to apply: ${err.message}. Your menu has not been modified.`, timestamp: new Date() }]);
    }
  }, [chatMode, messages, currentMenuData, getChangesWithApprovals, store, functions, appId]);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    const hasImages = attachedFiles.length > 0;
    if (!text && !hasImages) return;
    if (isSubmitting) return;

    if (applyState.phase === 'awaiting_confirm') {
      if (text.toLowerCase() === 'apply') {
        setInputValue('');
        setMessages(prev => [...prev, { id: generateId(), role: 'user' as MessageRole, content: 'apply', timestamp: new Date() }]);
        await executeApply((applyState as any).pendingMessageId);
        return;
      }
      if (text.toLowerCase() === 'cancel') {
        setInputValue('');
        cancelApply();
        setMessages(prev => [...prev,
          { id: generateId(), role: 'user' as MessageRole, content: 'cancel', timestamp: new Date() },
          { id: generateId(), role: 'assistant' as MessageRole, content: 'Cancelled — no changes made.', timestamp: new Date() },
        ]);
        return;
      }
      cancelApply();
    }

    setInputValue('');
    setIsSubmitting(true);
    let images: string[] = [];
    let imagePreviews: string[] = [];
    if (hasImages) {
      images = await filesToBase64(attachedFiles);
      imagePreviews = [...attachedPreviews];
      setAttachedFiles([]); setAttachedPreviews([]);
    }
    const userMsg: ChatMessage = { id: generateId(), role: 'user', content: text || `Attached ${images.length} image${images.length !== 1 ? 's' : ''}`, timestamp: new Date(), ...(imagePreviews.length && { images: imagePreviews }) };
    setMessages(prev => [...prev, userMsg]);
    const placeholderId = generateId();
    setMessages(prev => [...prev, { id: placeholderId, role: 'assistant', content: 'Thinking…', timestamp: new Date(), jobId: placeholderId }]);
    try {
      const context = buildConversationContext(messages.slice(-10));
      const instructions = `## Conversation History\n${context}\n\n## User's Latest Message\n${text || '(see attached images)'}`;
      const payload: any = { jobType: images.length > 0 ? 'menu_update' : 'chat_update', appId, storeId: store.storeId, menuNames: currentMenuData?.menuSettings?.map((ms: any) => ms.menuName) ?? ['Main Menu'], existingMenuData: compressMenuForChat(currentMenuData), instructions };
      if (images.length > 0) payload.images = images;
      const result = await httpsCallable(functions, 'submitMenuJob')(payload);
      const { jobId } = result.data as { jobId: string };
      setMessages(prev => prev.map(m => m.id === placeholderId ? { ...m, jobId } : m));
      setActiveJobId(jobId);
    } catch (err: any) {
      setMessages(prev => prev.map(m => m.id === placeholderId ? { ...m, content: 'Sorry, something went wrong. Please try again.' } : m));
      setIsSubmitting(false);
    }
  }, [inputValue, attachedFiles, attachedPreviews, isSubmitting, applyState, messages, functions, appId, store, currentMenuData, executeApply, cancelApply]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const totalItems = currentMenuData?.menu?.filter((i: any) => !i.isDeleted && i.menus?.[0] !== 'Additional Options').length ?? 0;
  const isAwaitingConfirm = applyState.phase === 'awaiting_confirm';
  const isApplying        = applyState.phase === 'applying';

  return (
    <div className="menu-chat-page">

      {/* ── Top bar ── */}
      <div className="menu-chat-topbar">
        <button className="menu-chat-back-btn" onClick={onBack}>← Back</button>
        <div className="menu-chat-store-info">
          <span className="menu-chat-store-name">{store.name}</span>
          {currentMenuData
            ? <span className="menu-chat-meta">{totalItems} items</span>
            : <span className="menu-chat-meta menu-chat-meta--empty">No menu yet</span>
          }
        </div>
        <div className="menu-chat-topbar-right">
          <span className="ai-badge">✦ AI Menu Chat</span>
          <span className="beta-badge">Beta</span>
          {/* Mobile-only preview toggle */}
          <button
            className="preview-toggle-btn"
            onClick={() => setShowMobilePreview(v => !v)}
            title="Toggle menu preview"
          >
            {showMobilePreview ? '💬 Chat' : '👁 Preview'}
          </button>
          {currentMenuData && (
            <button className="delete-menu-btn" onClick={() => setShowDeleteConfirm(true)} disabled={isSubmitting || isApplying}>
              🗑 Restart
            </button>
          )}
        </div>
      </div>

      {/* ── Delete confirm ── */}
      {showDeleteConfirm && (
        <div className="delete-overlay">
          <div className="delete-modal">
            <h3>Delete menu and start over?</h3>
            <p>This will wipe your current menu from the live store. A backup is saved to history.</p>
            <div className="delete-modal-actions">
              <button className="delete-cancel-btn" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>Cancel</button>
              <button className="delete-confirm-btn" onClick={handleDeleteMenu} disabled={isDeleting}>{isDeleting ? 'Deleting…' : 'Yes, delete and restart'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile full-screen preview overlay ── */}
      {showMobilePreview && (
        <div className="mobile-preview-overlay">
          <div className="mobile-preview-header">
            <span className="mobile-preview-title">Menu Preview</span>
            {applyState.phase === 'applied' && <span className="updated-badge">✓ Updated</span>}
            <button className="mobile-preview-close" onClick={() => setShowMobilePreview(false)}>✕ Close</button>
          </div>
          <div className="mobile-preview-body">
            {menuLoading
              ? <div className="app-preview-empty"><div className="app-preview-empty-icon">⏳</div><p>Loading live menu…</p></div>
              : <AppMenuViewer menuData={currentMenuData} storeName={store.name} />
            }
          </div>
        </div>
      )}

      {/* ── Split layout ── */}
      <div className="menu-chat-split">

        {/* Left: phone-frame app preview (desktop only) */}
        <div className="menu-chat-left">
          <div className="menu-chat-left-header">
            <h3>{chatMode === 'creation' ? 'Menu Preview' : 'Live Menu'}</h3>
            {applyState.phase === 'applied'
              ? <span className="updated-badge">✓ Updated</span>
              : <span className="read-only-badge">{chatMode === 'creation' ? 'Building…' : 'Preview'}</span>
            }
          </div>
          <div className="menu-chat-left-body">
            {menuLoading
              ? <div className="app-preview-empty"><div className="app-preview-empty-icon">⏳</div><p>Loading live menu…</p></div>
              : <AppMenuViewer menuData={currentMenuData} storeName={store.name} />
            }
          </div>
        </div>

        {/* Right: chat */}
        <div className="menu-chat-right">
          <div className="menu-chat-messages">

            {/* Intro card */}
            <div className="menu-chat-intro">
              <div className="intro-icon">✦</div>
              <div className="intro-text">
                {chatMode === 'creation' ? (
                  <>
                    <strong>Build Your Menu</strong>
                    <p>
                      Upload your menu images below. The AI will assign items to the menus listed —
                      remove <strong>Drinks Menu</strong> or <strong>Desserts Menu</strong> if you'd
                      rather keep everything in one menu. Add names like <em>Lunch Menu</em> or{' '}
                      <em>Breakfast Menu</em> if you have time-based menus.
                    </p>
                    {/* Menu name editor */}
                    <div className="creation-menu-names">
                      <div className="creation-menu-names-list">
                        {menuNames.map((name, i) => (
                          <div key={i} className="creation-menu-name-pill">
                            <span>{name}</span>
                            {menuNames.length > 1 && <button onClick={() => setMenuNames(prev => prev.filter((_, j) => j !== i))}>×</button>}
                          </div>
                        ))}
                      </div>
                      <div className="creation-menu-name-input-row">
                        <input type="text" placeholder="Add menu name (e.g. Lunch Menu, Breakfast Menu)" value={newMenuNameInput} onChange={e => setNewMenuNameInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && newMenuNameInput.trim()) { setMenuNames(prev => [...prev, newMenuNameInput.trim()]); setNewMenuNameInput(''); }}}
                          className="creation-menu-name-input" />
                        <button className="creation-menu-name-add-btn" disabled={!newMenuNameInput.trim()}
                          onClick={() => { if (newMenuNameInput.trim()) { setMenuNames(prev => [...prev, newMenuNameInput.trim()]); setNewMenuNameInput(''); }}}>Add</button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <strong>AI Menu Chat (Beta)</strong>
                    <p>
                      Describe changes in plain language, or attach new menu images to add items.
                      Toggle changes on/off, then click <strong>Apply</strong> and type <code>apply</code> to push live.
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Messages */}
            {messages.filter(m => m.role !== 'system').map(msg => {
              const changesWithApprovals = getChangesWithApprovals(msg);
              const approvedCount  = changesWithApprovals.filter(c => c.approved).length;
              const hasChanges     = msg.changeSet && msg.changeSet.changes.length > 0;
              const alreadyApplied = !!msg.appliedAt;
              const isPending      = isAwaitingConfirm && (applyState as any).pendingMessageId === (msg.jobId || msg.id);

              return (
                <div key={msg.id} className={`chat-message chat-message--${msg.role}`}>
                  {msg.role === 'assistant' && <div className="chat-message-avatar">✦</div>}
                  <div className="chat-message-body">
                    <p className="chat-message-text">{msg.content}</p>
                    {msg.images && msg.images.length > 0 && (
                      <div className="chat-message-images">
                        {msg.images.map((src, i) => src
                          ? <img key={i} src={src} alt={`attachment ${i+1}`} className="chat-message-thumb" />
                          : <div key={i} className="chat-message-thumb chat-message-thumb--pdf">📄 PDF</div>
                        )}
                      </div>
                    )}
                    {hasChanges && (
                      <div className={`chat-changeset ${alreadyApplied ? 'chat-changeset--applied' : ''} ${isPending ? 'chat-changeset--pending' : ''}`}>
                        <div className="chat-changeset-header">
                          <span className="changeset-count">{approvedCount} of {changesWithApprovals.length} change{changesWithApprovals.length !== 1 ? 's' : ''} selected</span>
                          <div className="changeset-header-right">
                            <span className={`changeset-mode ${msg.changeSet!.applyMode}`}>{msg.changeSet!.applyMode === 'auto' ? '⚡ Auto' : '👁 Review'}</span>
                            {alreadyApplied && <span className="changeset-applied-badge">✓ Applied</span>}
                          </div>
                        </div>
                        <div className="chat-changeset-list">
                          {changesWithApprovals.map(change => (
                            <ChangeCard key={change.id} change={change} showToggle={!alreadyApplied}
                              onToggle={!alreadyApplied ? (id) => toggleChangeApproval(msg.jobId || msg.id, id) : undefined} />
                          ))}
                        </div>
                        {!alreadyApplied && approvedCount > 0 && !isPending && !isApplying && (
                          <div className="changeset-apply-row">
                            <button className="apply-btn" onClick={() => initiateApply(msg.jobId || msg.id)}>
                              Apply {approvedCount} change{approvedCount !== 1 ? 's' : ''} to live menu →
                            </button>
                          </div>
                        )}
                        {isPending && (
                          <div className="changeset-confirm-prompt">
                            <span className="confirm-warning-icon">⚠</span>
                            <span>This will update your <strong>live menu</strong>. Type <code>apply</code> below to confirm, or <code>cancel</code>.</span>
                          </div>
                        )}
                        {isApplying && <div className="changeset-applying"><span className="chat-send-spinner" />Pushing to live menu…</div>}
                      </div>
                    )}
                    {msg.changeSet && msg.changeSet.changes.length === 0 && <div className="chat-no-changes">No structural changes proposed.</div>}
                  </div>
                </div>
              );
            })}

            {isSubmitting && !activeJobId && (
              <div className="chat-message chat-message--assistant">
                <div className="chat-message-avatar">✦</div>
                <div className="chat-message-body"><div className="chat-typing-indicator"><span /><span /><span /></div></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className={`menu-chat-input-area ${isAwaitingConfirm ? 'menu-chat-input-area--confirm' : ''}`}>
            {isAwaitingConfirm && (
              <div className="confirm-input-banner">
                ⚠ Type <strong>apply</strong> to push changes to your live menu, or <strong>cancel</strong> to go back.
              </div>
            )}
            {attachedFiles.length > 0 && (
              <div className="attachments-preview">
                {attachedFiles.map((file, i) => (
                  <div key={i} className="attachment-thumb-wrap">
                    {file.type.startsWith('image/') && attachedPreviews[i]
                      ? <img src={attachedPreviews[i]} alt={file.name} className="attachment-thumb" />
                      : <div className="attachment-thumb attachment-thumb--file">📄<span>{file.name.slice(0, 12)}</span></div>}
                    <button className="attachment-remove" onClick={() => removeAttachment(i)}>×</button>
                  </div>
                ))}
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple style={{ display: 'none' }} onChange={handleFileSelect} />

            {chatMode === 'creation' ? (
              <div className="input-row">
                <textarea ref={inputRef} className="menu-chat-input" value={inputValue} onChange={e => setInputValue(e.target.value)}
                  placeholder="Optional: add instructions before uploading…" rows={2} disabled={isSubmitting} />
                <button className="attach-btn" onClick={() => fileInputRef.current?.click()} disabled={isSubmitting} title="Attach menu images">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                </button>
                <button className="menu-chat-send-btn creation-send-btn" onClick={handleCreateMenuFromImages} disabled={attachedFiles.length === 0 || isSubmitting}>
                  {isSubmitting ? <span className="chat-send-spinner" /> : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>}
                </button>
              </div>
            ) : (
              <div className="input-row">
                <button className="attach-btn" onClick={() => fileInputRef.current?.click()} disabled={isSubmitting || isApplying || isAwaitingConfirm} title="Attach menu images">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                </button>
                <textarea ref={inputRef} className={`menu-chat-input ${isAwaitingConfirm ? 'menu-chat-input--confirm' : ''}`} value={inputValue}
                  onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder={isAwaitingConfirm ? 'Type "apply" to confirm or "cancel" to go back…' : attachedFiles.length > 0 ? 'Add instructions for these images, or send as-is…' : 'Describe a change or ask a question… (Enter to send)'}
                  rows={isAwaitingConfirm ? 1 : 3} disabled={isApplying} />
                <button className={`menu-chat-send-btn ${isAwaitingConfirm ? 'menu-chat-send-btn--confirm' : ''}`} onClick={handleSend} disabled={(!inputValue.trim() && attachedFiles.length === 0) || isApplying}>
                  {isApplying ? <span className="chat-send-spinner" />
                    : isAwaitingConfirm
                      ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      : <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuChat;